"use client";

import { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, Copy, RotateCcw } from "lucide-react";

import { formatBreadcrumbs } from "@/lib/diagnostics/breadcrumbs";
import { formatRenderTally } from "@/lib/diagnostics/render-tally";

/**
 * Pantalla de error de la app.
 *
 * Existe porque hasta ahora NO había ningún error boundary: cualquier excepción
 * de cliente dejaba el "Application error: a client-side exception has
 * occurred" pelado de Next, sin una sola pista. Con la app en producción y los
 * reportes llegando por WhatsApp ("se me rompió"), diagnosticar era arqueología
 * pura.
 *
 * Por eso el detalle técnico está a un toque de distancia y se puede COPIAR:
 * lo que se pide es una captura o un pegado, no que el usuario sepa abrir la
 * consola del navegador — cosa imposible en un celular.
 *
 * `digest` es clave para los errores de servidor: en producción Next reemplaza
 * el mensaje real por ese hash y con él se encuentra el stack completo en los
 * logs de Vercel.
 *
 * Presentacional y oscuro con colores explícitos: se usa también desde
 * `global-error`, que reemplaza el `<html>` entero y por lo tanto se renderiza
 * SIN los providers de tema.
 */
export function AppErrorScreen({
  error,
  reset,
  componentStack = null,
}: {
  error: Error & { digest?: string };
  reset?: () => void;
  /**
   * Cadena de componentes donde explotó. Sólo la tiene el boundary de clase
   * (`ClientErrorBoundary`); `error.tsx` de Next no la recibe. Es lo que
   * convierte un "#185" en "el que loopea es tal componente".
   */
  componentStack?: string | null;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const [copied, setCopied] = useState(false);

  /**
   * UNA foto de todo, tomada al montar la pantalla de error.
   *
   * Congelarlo importa: los clics que haga el usuario ACÁ (desplegar el
   * detalle, copiar) ensuciarían las migas, y los renders de esta misma
   * pantalla se mezclarían con los del lazo que se está diagnosticando.
   *
   * Va en un inicializador de `useState` y no suelto en el cuerpo porque
   * `performance.now()` y `new Date()` son impuras: llamarlas en cada render
   * daría un valor distinto por render (y el linter, con razón, lo marca).
   *
   * El ORDEN no es cosmético: un pegado en un chat se corta por abajo, así que
   * arriba va lo que más resuelve. Para un lazo de renders (#185) el ranking y
   * las migas valen más que el stack, que apunta a los internos de React.
   */
  const [snapshot] = useState(() => {
    const partes = [
      error.message || "Error sin mensaje",
      formatRenderTally() ? `renders: ${formatRenderTally()}` : null,
      formatBreadcrumbs() ? `pasos previos:\n${formatBreadcrumbs()}` : null,
      // Antes sólo iba el mensaje. En producción los nombres están minificados,
      // pero el ORDEN de las llamadas ya orienta — y con
      // `productionBrowserSourceMaps` se leen enteras.
      error.stack ? `stack:\n${error.stack.split("\n").slice(0, 10).join("\n")}` : null,
      componentStack ? `componentes:${componentStack.split("\n").slice(0, 12).join("\n")}` : null,
      error.digest ? `digest: ${error.digest}` : null,
      typeof window !== "undefined" ? `url: ${window.location.href}` : null,
      typeof navigator !== "undefined" ? `ua: ${navigator.userAgent}` : null,
      typeof window !== "undefined"
        ? `pantalla: ${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}`
        : null,
      // Distingue "explotó al entrar" de "explotó a los 20 minutos de uso".
      typeof performance !== "undefined"
        ? `tiempo en la página: ${(performance.now() / 1000).toFixed(0)}s`
        : null,
      `hora: ${new Date().toISOString()}`,
    ];
    return partes.filter(Boolean).join("\n");
  });

  const detail = snapshot;

  // A la consola también: si llega a haber devtools abiertas (o un celular
  // conectado por cable), acá está todo junto y expandible.
  useEffect(() => {
    console.error("[AppError]", error);
    console.error("[AppError] detalle:\n" + snapshot);
  }, [error, snapshot]);

  const copy = () => {
    // `clipboard` no existe en contextos inseguros ni en WebViews viejos, y
    // fallar acá dejaría la pantalla de error rota — que sería el colmo.
    void navigator.clipboard
      ?.writeText(detail)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 bg-[#060809] px-6 py-10 text-white">
      <span className="flex size-14 items-center justify-center rounded-full bg-amber-400/15 text-amber-300">
        <AlertTriangle className="size-7" aria-hidden="true" />
      </span>

      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-black tracking-tight">Se rompió algo</h1>
        <p className="max-w-sm text-sm text-neutral-400">
          No es culpa tuya. Podés reintentar; si vuelve a pasar, mandale el detalle de abajo a
          soporte.
        </p>
      </div>

      <div className="flex w-full max-w-sm flex-col gap-2">
        {reset ? (
          <button
            type="button"
            onClick={reset}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-bold text-primary-foreground transition hover:brightness-110"
          >
            <RotateCcw className="size-4" aria-hidden="true" />
            Reintentar
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            window.location.href = "/";
          }}
          className="h-11 w-full rounded-xl border border-white/15 text-sm font-bold text-neutral-200 transition hover:bg-white/10"
        >
          Volver al inicio
        </button>
      </div>

      <div className="w-full max-w-sm">
        <button
          type="button"
          onClick={() => setShowDetail((open) => !open)}
          aria-expanded={showDetail}
          className="flex w-full items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wide text-neutral-500 transition hover:text-neutral-300"
        >
          Detalle técnico
          <ChevronDown
            className={`size-3.5 transition-transform ${showDetail ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        {showDetail ? (
          <div className="mt-2 space-y-2">
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-white/10 bg-black/40 p-3 text-[11px] leading-relaxed text-neutral-300">
              {detail}
            </pre>
            <button
              type="button"
              onClick={copy}
              className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-white/15 text-xs font-bold text-neutral-300 transition hover:bg-white/10"
            >
              <Copy className="size-3.5" aria-hidden="true" />
              {copied ? "¡Copiado!" : "Copiar detalle"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
